/* ==========================================================================
   Plaud Teams hero motion — sections/plaud-teams-hero.liquid
   One container the whole way through: devices → tag (one line per step)
   → Team files card → Ask Plaud chat → back to the start.
   Content comes from the section markup; stages with no content are skipped.
   ========================================================================== */
(function () {
  if (window.PlaudTeamsHero) return;

  var STAGE_W = 620;
  var TAG_CHROME = 72.381;       // 12 + icon 34.438 + gap 11.483 + 12 + 2 x 1.23 border
  var ANSWER_VIEW = 150;         // px of visible answer before it scrolls
  var PEAK_IDLE = 0.34, PEAK_BURST = 0.92;
  var BOX_PEAK_IDLE = 0.12, BOX_PEAK_BURST = 0.22;
  var reduce = window.matchMedia('(prefers-reduced-motion: reduce)').matches;

  function init(root) {
    if (root._pth) return;

    var qa = function (sel) { return [].slice.call(root.querySelectorAll(sel)); };
    var q = function (sel) { return root.querySelector(sel); };

    var stage = q('.pth__stage');
    var ui = q('.pth-ui');
    var shell = q('.pth-shell');
    var slot = q('.pth-slot');
    var line = q('.pth-line');
    var typed = q('.pth-line > span');
    var caret = q('.pth-caret');
    var ripples = q('.pth-ripples');
    var devices = q('.pth-devices');
    var steps = qa('[data-pth-step]');
    /* a stage with no block has no markup; detached stand-ins keep the shared reset code branch-free */
    var spare = document.createElement('div');
    var bubble = q('.pth-bubble') || spare;
    var ansInner = q('.pth-answer__inner') || spare;
    var ansLines = qa('.pth-answer__line');
    var askPh = q('.pth-ask-ph') || spare;
    var askInput = q('.pth-ask-input') || spare;
    var askTyped = q('.pth-ask-input > span') || spare;
    var askCaret = q('.pth-ask-caret') || spare;

    var tags = qa('[data-pth-tag]').map(function (el) { return el.textContent.trim(); }).filter(Boolean);
    var query = bubble.textContent.trim();
    var hasCard = qa('.pth-row').length > 0;
    var hasChat = ansLines.length > 0 && query !== '';
    var loop = root.dataset.loop !== 'false';
    var pace = parseFloat(root.dataset.pace) || 1;

    var dead = false;
    var visible = true;
    var timers = [];
    var waiters = [];
    var pumpTimer = null;
    var observers = [];

    /* ---------- timing: scaled by pace, parked while off-screen or after destroy ---------- */
    function awake() { return visible && !document.hidden; }
    function flush() {
      if (!awake()) return;
      waiters.splice(0).forEach(function (resolve) { resolve(); });
    }
    function sleep(ms) {
      return new Promise(function (resolve) {
        var id = setTimeout(function () {
          if (dead) return;
          if (awake()) resolve(); else waiters.push(resolve);
        }, ms * pace);
        timers.push(id);
      });
    }
    document.addEventListener('visibilitychange', flush);

    /* ---------- scale the 620px stage to its container ---------- */
    function fit() {
      stage.style.setProperty('--pth-s', ((root.clientWidth || STAGE_W) / STAGE_W).toFixed(5));
      root.classList.add('is-ready');
    }
    fit();
    if (window.ResizeObserver) {
      var ro = new ResizeObserver(fit);
      ro.observe(root);
      observers.push(ro);
    }
    if (window.IntersectionObserver) {
      var io = new IntersectionObserver(function (entries) {
        visible = entries[entries.length - 1].isIntersecting;
        flush();
      });
      io.observe(root);
      observers.push(io);
    }

    var meas = document.createElement('span');
    meas.style.cssText = 'position:absolute;visibility:hidden;white-space:nowrap;font-size:13px;font-weight:400;';
    meas.style.fontFamily = getComputedStyle(line).fontFamily;
    stage.appendChild(meas);
    function width(str) { meas.textContent = str; return meas.offsetWidth; }

    /* the tag's width is the shell's width, they move together */
    function setTag(str, extra) {
      var sw = width(str) + (extra || 0);
      slot.style.setProperty('--tw', sw + 'px');
      shell.style.setProperty('--w', (TAG_CHROME + sw) + 'px');
    }
    function instant(on) {
      slot.classList.toggle('is-instant', on);
      shell.classList.toggle('is-instant', on);
    }

    /* ---------- concentric ripples that trace the current outline ---------- */
    var boostUntil = 0, shape = 'tag', paused = false, beat = 0;

    function spawn(dir, dur, tint) {
      var isBox = shape !== 'tag';
      var hot = Date.now() < boostUntil;
      var el = document.createElement('div');
      el.className = 'pth-ripple' + (isBox ? ' is-' + shape : '') + (tint ? ' is-tint' : '');
      el.style.setProperty('--peak', isBox ? (hot ? BOX_PEAK_BURST : BOX_PEAK_IDLE) : (hot ? PEAK_BURST : PEAK_IDLE));
      var name = 'pthRipple' + (isBox ? 'Box' : '') + (dir === 'out' ? 'Out' : 'In');
      el.style.animation = name + ' ' + dur + 'ms linear both';
      el.addEventListener('animationend', function () { el.remove(); });
      ripples.appendChild(el);
    }

    function clearRipples() {
      [].slice.call(ripples.children).forEach(function (el) {
        var cs = getComputedStyle(el);
        var op = cs.opacity, tr = cs.transform;
        el.style.animation = 'none';
        el.style.opacity = op;
        el.style.transform = tr;
        requestAnimationFrame(function () {
          el.style.transition = 'opacity .32s linear';
          el.style.opacity = '0';
          setTimeout(function () { el.remove(); }, 360);
        });
      });
    }

    var PUMP_MS = 1900, RIPPLE_MS = 4400;

    function pump() {
      if (dead || reduce || paused || !awake()) return;
      beat++;
      spawn('out', RIPPLE_MS, beat % 3 === 0);
    }

    /* a state change only brightens the ripples already in flight; the emitter's rhythm never changes */
    function burst() {
      if (reduce) return;
      boostUntil = Date.now() + 2000;
    }

    /* ---------- tag stage ---------- */
    async function type(str) {
      caret.classList.remove('is-gone', 'is-blink');
      instant(true);
      for (var i = 1; i <= str.length; i++) {
        typed.textContent = str.slice(0, i);
        setTag(str.slice(0, i), 14);
        await sleep(str[i - 1] === ' ' ? 52 : 28 + Math.random() * 20);
      }
      caret.classList.add('is-blink');
      await sleep(60);
      instant(false);
    }

    async function swap(text) {
      caret.classList.add('is-gone');
      var incoming = document.createElement('span');
      incoming.className = 'pth-line is-in';
      incoming.textContent = text;

      shell.classList.add('is-flash', 'is-done');
      burst();

      line.classList.add('is-out');
      slot.appendChild(incoming);
      setTag(text, 2);

      await sleep(520);
      line.remove();
      line = incoming;
      line.className = 'pth-line';
      await sleep(120);
      shell.classList.remove('is-flash');
    }

    /* replace the tag's single line out of band, while it is invisible */
    function setLine(str) {
      slot.innerHTML = '';
      line = document.createElement('span');
      line.className = 'pth-line';
      typed = document.createElement('span');
      typed.textContent = str;
      caret = document.createElement('i');
      caret.className = 'pth-caret ' + (str ? 'is-gone' : 'is-blink');
      line.appendChild(typed);
      line.appendChild(caret);
      slot.appendChild(line);
    }

    /* ---------- tag → Team files card ---------- */
    async function grow() {
      paused = true;
      clearRipples();

      shell.classList.add('is-morph');
      shell.style.removeProperty('--w');   // let .is-card drive the size
      shell.classList.add('is-card');
      shape = 'card';

      await sleep(560);                    // most of the way open
      paused = false;
      burst();
      await sleep(180);

      for (var i = 0; i < steps.length; i++) {
        steps[i].classList.add('is-in');
        await sleep(steps[i].classList.contains('pth-divider') ? 70 : 180);
      }
    }

    async function clearCard() {
      steps.forEach(function (el) { el.classList.remove('is-in'); });
      await sleep(260);                    // let the rows clear first
    }

    /* ---------- Team files card (or the tag) → Ask Plaud chat: one resize ---------- */
    async function toChat() {
      if (hasCard) await clearCard();

      paused = true;
      clearRipples();

      shell.style.removeProperty('--w');
      shell.classList.remove('is-morph', 'is-card');
      shell.classList.add('is-resize', 'is-chat');
      shape = 'chat';

      await sleep(620);                    // rings pick up the new outline mid-resize
      paused = false;
      burst();
      await sleep(420);
    }

    /* ---------- type into the Ask Plaud bar, send, answer ---------- */
    async function typeAsk(str) {
      askPh.classList.add('is-hidden');
      askInput.classList.add('is-shown');
      askCaret.classList.add('is-on');
      for (var i = 1; i <= str.length; i++) {
        askTyped.textContent = str.slice(0, i);
        await sleep(str[i - 1] === ' ' ? 48 : 26 + Math.random() * 18);
      }
      askCaret.classList.remove('is-on');
    }

    function scrollAnswer(el) {
      /* keep a small gap so the newest line never kisses the Ask Plaud bar */
      var dy = Math.min(0, (ANSWER_VIEW - 12) - (el.offsetTop + el.offsetHeight));
      ansInner.style.transform = 'translateY(' + dy + 'px)';
    }

    async function askAndAnswer() {
      await sleep(520);
      await typeAsk(query);
      await sleep(420);

      /* send: the bar empties and the message lands as a dark bubble */
      askInput.classList.remove('is-shown');
      askPh.classList.remove('is-hidden');
      askTyped.textContent = '';
      bubble.classList.add('is-in');
      shell.classList.add('is-flash');
      burst();
      await sleep(320);
      shell.classList.remove('is-flash');

      await sleep(560);                    // the beat before it answers

      for (var i = 0; i < ansLines.length; i++) {
        ansLines[i].classList.add('is-in');
        scrollAnswer(ansLines[i]);
        await sleep(i === 0 ? 420 : 380);
      }
    }

    function resetChat() {
      bubble.classList.remove('is-in');
      ansLines.forEach(function (el) { el.classList.remove('is-in'); });
      ansInner.style.transition = 'none';
      ansInner.style.transform = 'translateY(0px)';
    }

    /* ---------- back to an empty tag: the box empties, then one continuous gesture home ---------- */
    async function toTag() {
      if (shell.classList.contains('is-card')) await clearCard();
      shell.classList.add('is-clearing');
      await sleep(300);

      resetChat();

      paused = true;
      clearRipples();
      shell.classList.remove('is-resize', 'is-morph');
      shell.classList.add('is-reflow');

      setLine('');                         // empty tag, caret already blinking
      shell.classList.remove('is-done', 'is-chat', 'is-card', 'is-clearing');
      setTag('', 14);

      await sleep(480);                    // rings pick up the tag outline near the end
      shape = 'tag';
      paused = false;
      await sleep(120);
      burst();

      await sleep(300);
      shell.classList.remove('is-reflow');
      ansInner.style.transition = '';      // hand the transition back
    }

    /* ---------- devices intro: waves from the button, then hand over to the tag ---------- */
    var firstIntro = true;

    async function devicesIntro() {
      paused = true;
      if (!firstIntro) {
        devices.classList.remove('is-out');
        devices.classList.add('is-on');    // the two devices come back
      }
      await sleep(firstIntro ? 200 : 300);
      firstIntro = false;
      devices.classList.add('is-ping');
      await sleep(1500);

      devices.classList.add('is-out');     // devices step back…
      devices.classList.remove('is-on');
      await sleep(200);                    // let the device fade finish before the tag shows
      ui.classList.remove('is-away');      // …and the tag takes their place
      paused = false;
      burst();
      await sleep(560);
      devices.classList.remove('is-ping');
    }

    /* ---------- straight back to the devices ----------
       the UI steps away as the devices return, and the container is reset
       to an empty tag while nobody can see it                              */
    async function toDevices() {
      shell.classList.add('is-clearing');
      await sleep(300);

      paused = true;
      clearRipples();
      ui.classList.add('is-away');
      await sleep(520);                    // UI fade is .55s — devices wait so they never overlap it
      devices.classList.remove('is-out');
      devices.classList.add('is-on');
      await sleep(620);

      shell.classList.add('is-instant');
      resetChat();
      steps.forEach(function (el) { el.classList.remove('is-in'); });
      shell.classList.remove('is-resize', 'is-reflow', 'is-morph', 'is-chat', 'is-card', 'is-clearing', 'is-done', 'is-flash');
      setLine('');
      setTag('', 14);
      shape = 'tag';
      void shell.offsetWidth;
      shell.classList.remove('is-instant');
      ansInner.style.transition = '';
    }

    /* reduced motion: no loop, just the finished state */
    function showFinal() {
      ui.classList.remove('is-away');
      shell.classList.add('is-instant');
      shell.style.removeProperty('--w');
      if (hasChat) {
        shell.classList.add('is-chat');
        shape = 'chat';
        bubble.classList.add('is-in');
        ansLines.forEach(function (el) { el.classList.add('is-in'); });
        scrollAnswer(ansLines[ansLines.length - 1]);
      } else if (hasCard) {
        shell.classList.add('is-card');
        shape = 'card';
        steps.forEach(function (el) { el.classList.add('is-in'); });
      } else if (tags.length) {
        setLine(tags[tags.length - 1]);
        setTag(tags[tags.length - 1], 2);
        shell.classList.add('is-done');
      }
    }

    async function run() {
      if (reduce) { showFinal(); return; }

      setTag('', 14);
      pump();
      pumpTimer = setInterval(pump, PUMP_MS);

      do {
        if (devices) await devicesIntro();   // devices, pinging from the button → tag
        await sleep(250);

        for (var i = 0; i < tags.length; i++) {
          if (i === 0) await type(tags[i]); else await swap(tags[i]);
          await sleep(i === 0 && tags.length > 1 ? 180 : 1150);
        }

        if (hasCard) {
          await grow();                      // tag → Team files card
          await sleep(1250);
        }
        if (hasChat) {
          await toChat();                    // card (or tag) → Ask Plaud chat, one resize
          await askAndAnswer();
          await sleep(1900);
        } else {
          await sleep(1000);
        }

        if (!loop) return;                   // hold on the last frame
        if (devices) await toDevices(); else await toTag();
      } while (!dead);
    }

    root._pth = {
      destroy: function () {
        dead = true;
        timers.forEach(clearTimeout);
        clearInterval(pumpTimer);
        observers.forEach(function (o) { o.disconnect(); });
        document.removeEventListener('visibilitychange', flush);
        delete root._pth;
      }
    };

    if (document.fonts && document.fonts.ready) document.fonts.ready.then(run); else run();
  }

  function initAll(scope) {
    [].slice.call((scope || document).querySelectorAll('[data-pth]')).forEach(init);
  }

  window.PlaudTeamsHero = { init: init, initAll: initAll };

  if (document.readyState === 'loading') document.addEventListener('DOMContentLoaded', function () { initAll(); });
  else initAll();

  /* theme editor: re-run when the section (or one of its blocks) is re-rendered */
  document.addEventListener('shopify:section:load', function (e) { initAll(e.target); });
  document.addEventListener('shopify:section:unload', function (e) {
    [].slice.call(e.target.querySelectorAll('[data-pth]')).forEach(function (el) {
      if (el._pth) el._pth.destroy();
    });
  });
})();
